import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import formStyles from '../../components/domain/FormModal.module.css';
import { Button } from '../../components/ui/Button';
import { StatusPill } from '../../components/ui/StatusPill';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { curr } from '../../utils/price';
import {
  editCarrier,
  editPct,
  editShipper,
  lossAmount,
  previewMargin,
  resetToDefault,
  type PricingState,
} from './bidPricing';
import styles from './BidPricingBlock.module.css';

interface Props {
  value: PricingState;
  onChange: (next: PricingState) => void;
  /** Default shipper margin % from Settings; null while it loads. */
  defaultPct: number | null;
}

const signed = (n: number) => (n < 0 ? `−${curr(-n)}` : curr(n));

/** "Carrier gets ₹ / Shipper pays ₹" with a live margin preview (#221, R10 step A). */
export function BidPricingBlock({ value, onChange, defaultPct }: Props) {
  const { t } = useTranslation();
  const [showPct, setShowPct] = useState(false);
  const margin = previewMargin(value);
  const loss = lossAmount(value);

  return (
    <div className={styles.block}>
      <div className={formStyles.row}>
        <div>
          <label className={formStyles.label} htmlFor="bid-carrier-price">
            {t('shipments.bidPricingBlock.carrierLabel')}
          </label>
          <input
            id="bid-carrier-price"
            className={formStyles.input}
            type="number"
            min="0"
            inputMode="numeric"
            value={value.carrier}
            onChange={(e) => onChange(editCarrier(value, e.target.value))}
            placeholder="48000"
          />
        </div>
        <div>
          <label className={formStyles.label} htmlFor="bid-shipper-price">
            {t('shipments.bidPricingBlock.shipperLabel')}
          </label>
          <input
            id="bid-shipper-price"
            className={formStyles.input}
            type="number"
            inputMode="numeric"
            value={value.shipper}
            onChange={(e) => onChange(editShipper(value, e.target.value))}
            placeholder="50400"
          />
        </div>
      </div>

      <div className={styles.marginLine} aria-live="polite">
        <span>{t('shipments.bidPricingBlock.margin')}</span>
        <span className={styles.marginValue} data-testid="bid-margin-preview">
          {margin ? `${signed(margin.amount)} · ${margin.pct}` : '—'}
        </span>
        <span className={styles.previewTag}>{t('shipments.bidPricingBlock.preview')}</span>
        {value.fixed && (
          <StatusPill
            bg="var(--status-gray-bg)"
            text="var(--status-gray-text)"
            label={t('shipments.bidPricingBlock.fixed')}
            title={t('shipments.bidPricingBlock.fixedTitle')}
            icon={<StatusIcon name="edit" />}
          />
        )}
        <span className={styles.actions}>
          <Button variant="ghost" size="sm" onClick={() => setShowPct((v) => !v)} aria-expanded={showPct}>
            {t('shipments.bidPricingBlock.editPct')}
          </Button>
          {value.fixed && (
            <Button
              variant="ghost"
              size="sm"
              disabled={defaultPct == null}
              onClick={() => defaultPct != null && onChange(resetToDefault(value, defaultPct))}
            >
              {t('shipments.bidPricingBlock.resetToDefault')}
            </Button>
          )}
        </span>
      </div>

      {showPct && (
        <div className={styles.pctRow}>
          <label className={formStyles.label} htmlFor="bid-margin-pct">
            {t('shipments.bidPricingBlock.pctLabel')}
          </label>
          <input
            id="bid-margin-pct"
            className={formStyles.input}
            type="number"
            step="0.01"
            value={value.pct}
            onChange={(e) => onChange(editPct(value, e.target.value))}
            placeholder={defaultPct != null ? String(defaultPct) : '5'}
          />
          <p className={formStyles.hint}>{t('shipments.bidPricingBlock.pctHint')}</p>
        </div>
      )}

      {loss != null && (
        <div className={styles.loss} role="status">
          <StatusPill
            bg="var(--status-red-bg)"
            text="var(--status-red-text)"
            label={t('shipments.bidPricingBlock.loss', { amount: curr(loss) })}
            icon={<StatusIcon name="close" />}
          />
          <span>{t('shipments.bidPricingBlock.lossHint')}</span>
        </div>
      )}
    </div>
  );
}
